/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useAuth } from '../components/Auth'
import { PageHeader, State } from '../components/UI'
import { api, businessPath, dateTime, money } from '../lib/api'

type NumberValue = number | string | null | undefined

type Branch = {
  id: string
  name: string
}

type ReportKind =
  | 'summary'
  | 'profit-loss'
  | 'sales'
  | 'sales-products'
  | 'sales-staff'
  | 'sales-branches'
  | 'purchases'
  | 'receivables'
  | 'payables'
  | 'expenses'
  | 'other-income'
  | 'payroll'
  | 'inventory-value'
  | 'stock-history'

type SalesSummary = {
  sale_count: number
  revenue: NumberValue
  amount_paid: NumberValue
  outstanding: NumberValue
}

type PurchaseSummary = {
  purchase_count: number
  total: NumberValue
  amount_paid: NumberValue
  outstanding: NumberValue
}

type ProfitLoss = {
  revenue: NumberValue
  cost_of_goods_sold: NumberValue
  gross_profit: NumberValue
  expenses: NumberValue
  other_income: NumberValue
  net_profit: NumberValue
  cogs_basis?: string
}

type Receivables = {
  total_outstanding: NumberValue
  buckets: {
    current: NumberValue
    '1_30': NumberValue
    '31_60': NumberValue
    '61_90': NumberValue
    '90_plus': NumberValue
  }
}

type Summary = {
  sales: SalesSummary
  purchases: PurchaseSummary
  profit_loss: ProfitLoss
  receivables: Receivables
}

type ProductSale = {
  product_id: string
  product_name: string
  quantity: NumberValue
  revenue: NumberValue
}

type GroupedSale = {
  staff_id?: string
  branch_id?: string
  sale_count: number
  revenue: NumberValue
}

type InventoryItem = {
  product_id: string
  quantity: NumberValue
  unit_cost: NumberValue
  value: NumberValue
}

type InventoryValue = {
  total_value: NumberValue
  items: InventoryItem[]
}

type StockMovement = {
  id: string
  branch_id: string
  product_id: string
  variant_id?: string | null
  quantity_changed: NumberValue
  previous_quantity: NumberValue
  resulting_quantity: NumberValue
  reason: string
  comment?: string | null
  performed_by_user_id?: string | null
  created_at: string
}

type TotalReport = {
  total?: NumberValue
  total_paid?: NumberValue
}

type ReportConfig = {
  title: string
  description: string
  endpoint: string
}

const configs: Record<ReportKind, ReportConfig> = {
  summary: {
    title: 'Report Summary',
    description: 'Business performance summary from real workspace data.',
    endpoint: 'summary',
  },
  'profit-loss': {
    title: 'Profit & Loss',
    description: 'Revenue, cost of goods, expenses, other income and net profit.',
    endpoint: 'profit-loss',
  },
  sales: {
    title: 'Sales Report',
    description: 'Completed sales totals, collections and outstanding balances.',
    endpoint: 'sales',
  },
  'sales-products': {
    title: 'Sales by Product',
    description: 'Products ranked by recorded sales revenue.',
    endpoint: 'sales-products',
  },
  'sales-staff': {
    title: 'Sales by Staff',
    description: 'Completed sales grouped by the staff member who created them.',
    endpoint: 'sales-staff',
  },
  'sales-branches': {
    title: 'Sales by Branch',
    description: 'Completed sales grouped by branch.',
    endpoint: 'sales-branches',
  },
  purchases: {
    title: 'Purchases Report',
    description: 'Purchase totals, payments and supplier balances.',
    endpoint: 'purchases',
  },
  receivables: {
    title: 'Receivables Aging',
    description: 'Outstanding customer balances grouped by age.',
    endpoint: 'receivables',
  },
  payables: {
    title: 'Payables Report',
    description: 'Purchase balances currently outstanding to suppliers.',
    endpoint: 'payables',
  },
  expenses: {
    title: 'Expenses Report',
    description: 'Total active expenses for the selected period.',
    endpoint: 'expenses',
  },
  'other-income': {
    title: 'Other Income Report',
    description: 'Total active non-sales income for the selected period.',
    endpoint: 'other-income',
  },
  payroll: {
    title: 'Payroll Report',
    description: 'Total value of payroll runs recorded as paid.',
    endpoint: 'payroll',
  },
  'inventory-value': {
    title: 'Inventory Value',
    description: 'Current stock quantity valued using product purchase price.',
    endpoint: 'inventory-value',
  },
  'stock-history': {
    title: 'Stock History Report',
    description: 'Recorded stock adjustments for the selected period.',
    endpoint: 'stock-history',
  },
}

const reportLinks: { kind: ReportKind; note: string }[] = [
  { kind: 'summary', note: 'Sales, purchases, profit and receivables together.' },
  { kind: 'profit-loss', note: 'Revenue, COGS, expenses and net profit.' },
  { kind: 'sales', note: 'Sales totals, paid amount and balance.' },
  { kind: 'sales-products', note: 'Revenue and quantity by product.' },
  { kind: 'sales-staff', note: 'Sales grouped by staff.' },
  { kind: 'sales-branches', note: 'Sales grouped by branch.' },
  { kind: 'purchases', note: 'Purchasing activity and balances.' },
  { kind: 'receivables', note: 'Customer debt aging buckets.' },
  { kind: 'payables', note: 'Outstanding purchase balances.' },
  { kind: 'expenses', note: 'Recorded business expenses.' },
  { kind: 'other-income', note: 'Non-sales business income.' },
  { kind: 'payroll', note: 'Paid payroll totals.' },
  { kind: 'inventory-value', note: 'Current inventory valuation.' },
  { kind: 'stock-history', note: 'Stock adjustment history.' },
]

const shortId = (value?: string | null) =>
  value ? value.slice(0, 8) : '—'

const amount = (value: NumberValue) => money(value ?? 0)

const quantity = (value: NumberValue) => {
  const number = Number(value ?? 0)
  return new Intl.NumberFormat('en-NG', {
    maximumFractionDigits: 4,
  }).format(number)
}

const Tile = ({
  label,
  value,
  moneyValue = true,
}: {
  label: string
  value: NumberValue
  moneyValue?: boolean
}) => (
  <article className="aad-card aad-card-padding">
    <p className="aad-muted">{label}</p>
    <h2 className="aad-section-title">
      {moneyValue ? amount(value) : quantity(value)}
    </h2>
  </article>
)

export default function ReportsPage({ kind }: { kind?: ReportKind }) {
  const { workspace } = useAuth()

  const branches =
    (
      workspace as typeof workspace & {
        accessible_branches?: Branch[]
      }
    ).accessible_branches ??
    (workspace.main_branch ? [workspace.main_branch] : [])

  const [branchId, setBranchId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [data, setData] = useState<unknown>(null)
  const [loading, setLoading] = useState(Boolean(kind))
  const [error, setError] = useState('')

  const branchName = (id?: string | null) =>
    branches.find((branch) => branch.id === id)?.name ??
    (id ? `Branch ${shortId(id)}` : '—')

  const load = async (
    override?: {
      branchId: string
      startDate: string
      endDate: string
    },
  ) => {
    if (!kind) return

    const active = override ?? { branchId, startDate, endDate }

    if (
      active.startDate &&
      active.endDate &&
      active.endDate < active.startDate
    ) {
      setError('End date cannot be before start date.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const query = new URLSearchParams()

      if (active.branchId) query.set('branch_id', active.branchId)
      if (active.startDate) query.set('start_date', active.startDate)
      if (active.endDate) query.set('end_date', active.endDate)

      const suffix = query.toString() ? `?${query.toString()}` : ''

      const response = await api<unknown>(
        businessPath(`/reports/${configs[kind].endpoint}${suffix}`),
      )

      setData(response)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load this report.',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (kind) void load()
  }, [kind])

  const clearFilters = () => {
    setBranchId('')
    setStartDate('')
    setEndDate('')

    void load({
      branchId: '',
      startDate: '',
      endDate: '',
    })
  }

  if (!kind) {
    return (
      <section className="aad-page">
        <PageHeader
          title="Reports"
          description="Real business reports calculated from your AAD Business App records."
        />

        <div className="detail-grid">
          {reportLinks.map((item) => (
            <Link
              key={item.kind}
              to={`/reports/${item.kind}`}
              className="aad-card aad-card-padding"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <h2 className="aad-section-title">
                {configs[item.kind].title}
              </h2>
              <p className="aad-muted">{item.note}</p>
              <span className="aad-button aad-button-secondary">
                Open report
              </span>
            </Link>
          ))}
        </div>
      </section>
    )
  }

  const config = configs[kind]

  const filters = (
    <section className="aad-card aad-card-padding">
      <h2 className="aad-section-title">Filters</h2>

      <div className="aad-form-grid">
        <label className="aad-field">
          <span className="aad-label">Branch</span>
          <select
            className="aad-select"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
          >
            <option value="">All accessible branches</option>
            {branches.map((branch) => (
              <option value={branch.id} key={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </label>

        <label className="aad-field">
          <span className="aad-label">Start date</span>
          <input
            className="aad-input"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>

        <label className="aad-field">
          <span className="aad-label">End date</span>
          <input
            className="aad-input"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
      </div>

      <div className="form-actions">
        <button
          className="aad-button aad-button-gold"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? 'Loading…' : 'Apply filters'}
        </button>

        <button
          className="aad-button aad-button-secondary"
          disabled={loading}
          onClick={clearFilters}
        >
          Clear
        </button>
      </div>
    </section>
  )

  const renderSales = (report: SalesSummary) => (
    <div className="detail-grid">
      <Tile
        label="Completed sales"
        value={report.sale_count}
        moneyValue={false}
      />
      <Tile label="Revenue" value={report.revenue} />
      <Tile label="Amount paid" value={report.amount_paid} />
      <Tile label="Outstanding" value={report.outstanding} />
    </div>
  )

  const renderPurchases = (report: PurchaseSummary) => (
    <div className="detail-grid">
      <Tile
        label="Purchases"
        value={report.purchase_count}
        moneyValue={false}
      />
      <Tile label="Purchase total" value={report.total} />
      <Tile label="Amount paid" value={report.amount_paid} />
      <Tile label="Outstanding" value={report.outstanding} />
    </div>
  )

  const renderProfitLoss = (report: ProfitLoss) => (
    <>
      <div className="detail-grid">
        <Tile label="Revenue" value={report.revenue} />
        <Tile
          label="Cost of goods sold"
          value={report.cost_of_goods_sold}
        />
        <Tile label="Gross profit" value={report.gross_profit} />
        <Tile label="Expenses" value={report.expenses} />
        <Tile label="Other income" value={report.other_income} />
        <Tile label="Net profit" value={report.net_profit} />
      </div>

      {report.cogs_basis && (
        <section className="aad-card aad-card-padding">
          <h2 className="aad-section-title">
            Cost calculation note
          </h2>
          <p className="aad-muted">{report.cogs_basis}</p>
        </section>
      )}
    </>
  )

  const renderReceivables = (report: Receivables) => (
    <>
      <div className="detail-grid">
        <Tile
          label="Total outstanding"
          value={report.total_outstanding}
        />
        <Tile label="Current" value={report.buckets.current} />
        <Tile label="1–30 days" value={report.buckets['1_30']} />
        <Tile label="31–60 days" value={report.buckets['31_60']} />
        <Tile label="61–90 days" value={report.buckets['61_90']} />
        <Tile label="90+ days" value={report.buckets['90_plus']} />
      </div>
    </>
  )

  const renderProductSales = (rows: ProductSale[]) => (
    <section className="aad-card aad-card-padding">
      <h2 className="aad-section-title">Product performance</h2>

      {!rows.length ? (
        <p className="aad-muted">
          No completed product sales match these filters.
        </p>
      ) : (
        <div className="aad-table-wrap">
          <table className="aad-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.product_id}>
                  <td>
                    <Link to={`/products/${row.product_id}`}>
                      {row.product_name}
                    </Link>
                  </td>
                  <td>{quantity(row.quantity)}</td>
                  <td>{amount(row.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )

  const renderGroupedSales = (
    rows: GroupedSale[],
    group: 'staff' | 'branch',
  ) => (
    <section className="aad-card aad-card-padding">
      <h2 className="aad-section-title">
        {group === 'staff' ? 'Sales by staff' : 'Sales by branch'}
      </h2>

      {!rows.length ? (
        <p className="aad-muted">
          No completed sales match these filters.
        </p>
      ) : (
        <div className="aad-table-wrap">
          <table className="aad-table">
            <thead>
              <tr>
                <th>{group === 'staff' ? 'Staff' : 'Branch'}</th>
                <th>Sales</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const id =
                  group === 'staff'
                    ? row.staff_id
                    : row.branch_id

                return (
                  <tr key={`${id ?? 'unknown'}-${index}`}>
                    <td>
                      {group === 'branch'
                        ? branchName(id)
                        : `Staff ${shortId(id)}`}
                    </td>
                    <td>{row.sale_count}</td>
                    <td>{amount(row.revenue)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )

  const renderInventory = (report: InventoryValue) => (
    <>
      <div className="detail-grid">
        <Tile label="Total inventory value" value={report.total_value} />
        <Tile
          label="Products with stock"
          value={report.items.length}
          moneyValue={false}
        />
      </div>

      <section className="aad-card aad-card-padding">
        <h2 className="aad-section-title">Inventory valuation</h2>

        {!report.items.length ? (
          <p className="aad-muted">
            No inventory records match these filters.
          </p>
        ) : (
          <div className="aad-table-wrap">
            <table className="aad-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Unit cost</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {report.items.map((row) => (
                  <tr key={row.product_id}>
                    <td>
                      <Link to={`/products/${row.product_id}`}>
                        Product {shortId(row.product_id)}
                      </Link>
                    </td>
                    <td>{quantity(row.quantity)}</td>
                    <td>{amount(row.unit_cost)}</td>
                    <td>{amount(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="aad-muted">
          Inventory is valued using the product purchase price currently
          stored in AAD Business App.
        </p>
      </section>
    </>
  )

  const renderStockHistory = (rows: StockMovement[]) => (
    <section className="aad-card aad-card-padding">
      <h2 className="aad-section-title">Stock adjustments</h2>

      {!rows.length ? (
        <p className="aad-muted">
          No stock adjustments match these filters.
        </p>
      ) : (
        <div className="aad-table-wrap">
          <table className="aad-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Product</th>
                <th>Branch</th>
                <th>Reason</th>
                <th>Change</th>
                <th>Before → after</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{dateTime(row.created_at)}</td>
                  <td>
                    <Link to={`/products/${row.product_id}`}>
                      Product {shortId(row.product_id)}
                    </Link>
                  </td>
                  <td>{branchName(row.branch_id)}</td>
                  <td>{row.reason.replaceAll('_', ' ')}</td>
                  <td>{quantity(row.quantity_changed)}</td>
                  <td>
                    {quantity(row.previous_quantity)}
                    {' → '}
                    {quantity(row.resulting_quantity)}
                  </td>
                  <td>{row.comment || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="aad-muted">
        This report uses recorded stock adjustments. Branch-to-branch
        transfer movements are available in Inventory History.
      </p>
    </section>
  )

  const renderReport = () => {
    if (data === null) return null

    switch (kind) {
      case 'summary': {
        const report = data as Summary

        return (
          <>
            <section className="aad-card aad-card-padding">
              <h2 className="aad-section-title">Sales</h2>
              {renderSales(report.sales)}
            </section>

            <section className="aad-card aad-card-padding">
              <h2 className="aad-section-title">Purchases</h2>
              {renderPurchases(report.purchases)}
            </section>

            <section className="aad-card aad-card-padding">
              <h2 className="aad-section-title">
                Profit & loss
              </h2>
              {renderProfitLoss(report.profit_loss)}
            </section>

            <section className="aad-card aad-card-padding">
              <h2 className="aad-section-title">
                Receivables
              </h2>
              {renderReceivables(report.receivables)}
            </section>
          </>
        )
      }

      case 'profit-loss':
        return renderProfitLoss(data as ProfitLoss)

      case 'sales':
        return renderSales(data as SalesSummary)

      case 'sales-products':
        return renderProductSales(data as ProductSale[])

      case 'sales-staff':
        return renderGroupedSales(data as GroupedSale[], 'staff')

      case 'sales-branches':
        return renderGroupedSales(data as GroupedSale[], 'branch')

      case 'purchases':
      case 'payables':
        return renderPurchases(data as PurchaseSummary)

      case 'receivables':
        return renderReceivables(data as Receivables)

      case 'expenses':
        return (
          <div className="detail-grid">
            <Tile
              label="Total expenses"
              value={(data as TotalReport).total}
            />
          </div>
        )

      case 'other-income':
        return (
          <div className="detail-grid">
            <Tile
              label="Total other income"
              value={(data as TotalReport).total}
            />
          </div>
        )

      case 'payroll':
        return (
          <div className="detail-grid">
            <Tile
              label="Total payroll paid"
              value={(data as TotalReport).total_paid}
            />
          </div>
        )

      case 'inventory-value':
        return renderInventory(data as InventoryValue)

      case 'stock-history':
        return renderStockHistory(data as StockMovement[])
    }
  }

  return (
    <section className="aad-page">
      <PageHeader
        title={config.title}
        description={config.description}
        action={
          <div className="header-actions">
            <Link
              className="aad-button aad-button-secondary"
              to="/reports"
            >
              All reports
            </Link>

            <button
              className="aad-button aad-button-secondary"
              type="button"
              onClick={() => window.print()}
            >
              Print
            </button>
          </div>
        }
      />

      {error && <State title={error} error />}

      {filters}

      {loading && data === null ? (
        <State title="Loading report…" />
      ) : (
        renderReport()
      )}
    </section>
  )
}
